import { ADJUST_LOOKS, AdjustAdjustments, AdjustLook, AdjustmentKey, DEFAULT_ADJUSTMENTS } from './adjust.types'

/** Fine tune + the look at its intensity: a look is a delta on top of the sliders, so it never hides them */
export const mergeAdjustments = (adjustments: AdjustAdjustments, look: AdjustLook): AdjustAdjustments => {
  const preset = ADJUST_LOOKS.find(item => item.id === look.id)
  if (!preset || look.id === 'original') return adjustments
  const merged = { ...adjustments }
  const factor = look.intensity / 100
  for (const key of Object.keys(preset.adjustments) as AdjustmentKey[]) {
    merged[key] = merged[key] + (preset.adjustments[key] ?? 0) * factor
  }
  return merged
}

export const isNeutral = (adjustments: AdjustAdjustments): boolean =>
  (Object.keys(DEFAULT_ADJUSTMENTS) as AdjustmentKey[]).every(key => adjustments[key] === DEFAULT_ADJUSTMENTS[key])

/** Clarity's local-contrast radius follows the photo's size, so the preview and the saved file look the same */
export const clarityRadiusFor = (width: number, height: number): number => Math.max(1, Math.round(Math.max(width, height) * 0.002))

const smoothstep = (edge0: number, edge1: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)))
  return t * t * (3 - 2 * t)
}

/** Uniform values shared by the CPU path and the WebGL shader (same scaling as the in-house editor) */
export const toShaderUniforms = (a: AdjustAdjustments) => ({
  exposure: a.exposure / 100,
  brightness: a.brightness / 100,
  contrast: a.contrast / 100,
  highlights: a.highlights / 100,
  shadows: a.shadows / 100,
  saturation: a.saturation / 100,
  temperature: a.temperature / 100,
  tint: a.tint / 100,
  gamma: a.gamma || 1,
  clarity: a.clarity / 100,
  vignette: a.vignette / 100,
})

/**
 * CPU version of the adjustment shader, for look thumbnails and when WebGL is unavailable. Same order of operations
 * as the in-house shader, with clarity corrected: the detail is the SOURCE minus its local average (the shader it
 * comes from subtracted unadjusted neighbours from the adjusted pixel, so clarity amplified every other slider).
 */
export const processPixels = (
  source: Uint8ClampedArray,
  width: number,
  height: number,
  adjustments: AdjustAdjustments,
  out: Uint8ClampedArray
): void => {
  const u = toShaderUniforms(adjustments)
  const exposure = Math.pow(2, u.exposure)
  const contrast = 1 + u.contrast
  const saturation = 1 + u.saturation
  const warm = u.temperature * 0.1
  const tint = u.tint * 0.1
  const inverseGamma = 1 / u.gamma
  const doGamma = Math.abs(u.gamma - 1) > 1e-3
  const clarity = u.clarity * 1.5
  const radius = clarityRadiusFor(width, height)

  for (let y = 0; y < height; y++) {
    const up = Math.max(0, y - radius) * width
    const down = Math.min(height - 1, y + radius) * width
    const row = y * width
    for (let x = 0; x < width; x++) {
      const i = (row + x) * 4
      const r0 = source[i] / 255
      const g0 = source[i + 1] / 255
      const b0 = source[i + 2] / 255

      let r = r0 * exposure + u.brightness
      let g = g0 * exposure + u.brightness
      let b = b0 * exposure + u.brightness

      r = (r - 0.5) * contrast + 0.5
      g = (g - 0.5) * contrast + 0.5
      b = (b - 0.5) * contrast + 0.5

      const lum = 0.299 * r + 0.587 * g + 0.114 * b
      if (u.highlights) {
        const m = smoothstep(0.5, 1, lum) * u.highlights * 0.5
        r += m; g += m; b += m
      }
      if (u.shadows) {
        const m = (1 - smoothstep(0, 0.5, lum)) * u.shadows * 0.5
        r += m; g += m; b += m
      }

      const gray = 0.299 * r + 0.587 * g + 0.114 * b
      r = gray + (r - gray) * saturation
      g = gray + (g - gray) * saturation
      b = gray + (b - gray) * saturation

      r += warm
      b -= warm
      g += tint

      if (doGamma) {
        r = Math.pow(Math.max(r, 0), inverseGamma)
        g = Math.pow(Math.max(g, 0), inverseGamma)
        b = Math.pow(Math.max(b, 0), inverseGamma)
      }

      if (clarity) {
        const left = Math.max(0, x - radius)
        const right = Math.min(width - 1, x + radius)
        const a1 = (up + left) * 4
        const a2 = (up + right) * 4
        const a3 = (down + left) * 4
        const a4 = (down + right) * 4
        r += (r0 - (source[a1] + source[a2] + source[a3] + source[a4]) / 1020) * clarity
        g += (g0 - (source[a1 + 1] + source[a2 + 1] + source[a3 + 1] + source[a4 + 1]) / 1020) * clarity
        b += (b0 - (source[a1 + 2] + source[a2 + 2] + source[a3 + 2] + source[a4 + 2]) / 1020) * clarity
      }

      if (u.vignette) {
        const dx = x / width - 0.5
        const dy = y / height - 0.5
        const factor = 1 - smoothstep(0.5, 1.5, Math.sqrt(dx * dx + dy * dy) * 1.414) * u.vignette
        r *= factor; g *= factor; b *= factor
      }

      // Uint8ClampedArray clamps and rounds
      out[i] = r * 255
      out[i + 1] = g * 255
      out[i + 2] = b * 255
      out[i + 3] = source[i + 3]
    }
  }
}
